// Proposes how a Room Template's LEGACY scope values (the Architectural
// dropdowns wall_system / ceiling_system / floor_finish / coving and the
// door / vision-panel / pass-through counts) become real Standard Elements
// against the catalog. Pure: it only PROPOSES — ranked candidates, what would
// change on the catalog row, what would be written to the template. Nothing is
// chosen for the estimator unless exactly one candidate stands clearly ahead,
// and the admin confirms every row before anything is saved.
import { inferElementBasis, entityKeyOf } from "../config/roomElements";
import { wallSystemTypes } from "./wallTypes";

// One entry per legacy field: how to recognise a catalog row for it, the unit
// it prices in, and (area / length elements) the quantity basis the element
// needs so a room's quantity is calculated from its geometry, not typed.
export const LEGACY_FIELDS = {
  wall_system:    { label: "Wall panel system", wall: true, words: /wall|panel|liner|honeycomb|drywall|frp|mgo/i },
  ceiling_system: { label: "Ceiling system", re: /ceiling/i, uom: "sf", basis: "ceiling_area", exclude: /labor|hanging|support|steel grid|fall protection|led lighting|demo/i },
  floor_finish:   { label: "Floor finish", re: /floor|epoxy|vinyl|flooring/i, uom: "sf", basis: "floor_area", exclude: /labor|striping|drain|bumper|demo/i },
  coving:         { label: "Coving", re: /cov/i, uom: "lf", basis: "perimeter", exclude: /labor|demo/i },
  door_count:     { label: "Personnel doors", re: /door/i, uom: "ea", count: true, exclude: /labor|roll|overhead|hollow|core and shell|demo|infill|interlock|rail|install|storefront|frames/i },
  window_count:   { label: "Vision panels", re: /window|vision/i, uom: "ea", count: true, exclude: /labor|treatment|film|demo|storefront|maintenance/i },
  pass_throughs:  { label: "Pass-throughs", re: /pass[- ]?thru|pass[- ]?through/i, uom: "ea", count: true, exclude: /labor|power wiring|demo/i },
};

const NO_ELEMENT_VALUES = /^(none|by others|not required)$/i;
const norm = (s) => String(s || "").toLowerCase();
const wordsOf = (s) => norm(s).replace(/[^a-z0-9]+/g, " ").split(" ").filter(w => w.length > 2);
const nameOf = (e) => e.Kind === "item" ? e.ItemName : e.Description;
const isActive = (e) => e.IsActive !== false && !/\binactive\b/i.test(nameOf(e) || "");

function candidatesFor(field, legacyValue, entities) {
  const def = LEGACY_FIELDS[field];
  const optionWords = def.count ? [] : wordsOf(legacyValue);
  return entities
    .filter(isActive)
    .filter(e => !(def.exclude && def.exclude.test(nameOf(e) || "")))
    .map(e => {
      const name = nameOf(e) || "";
      let score = 0;
      if (def.re && def.re.test(name)) score += 3;
      if (def.uom && norm(e.UOM) === def.uom) score += 2;
      else if (def.uom && e.UOM) score -= 3;                                    // priced per a different unit than this scope is measured in
      if (e.Code && /^[A-Z]{1,4}-?[A-Z0-9]*\d/.test(e.Code)) score += 1;   // a short catalog code (C-1, D-2), not an imported estimate line
      if (String(e.Trade || "").toLowerCase() === "architectural") score += 1;
      if (e.IsRoomElement) score += 2;
      score += 2 * optionWords.filter(w => norm(name).includes(w)).length;
      if (/^'/.test(name)) score -= 3;                                          // imported estimate rows start with an apostrophe
      return { entity: e, score };
    })
    .filter(c => c.score >= 5 && (!def.re || def.re.test(nameOf(c.entity) || "")))
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);
}

/** What flagging a candidate as a Room element would change on its catalog row (null = already fine). */
export function flagPatchFor(entity, field) {
  const def = LEGACY_FIELDS[field];
  if (!def || def.wall) return null;
  const patch = {};
  if (!entity.IsRoomElement) patch.IsRoomElement = true;
  if (!entity.ElementDiscipline) patch.ElementDiscipline = "arch";
  if (def.basis && inferElementBasis(entity) !== def.basis) patch.ElementQuantityBasis = def.basis;
  return Object.keys(patch).length ? patch : null;
}

/**
 * -> one proposal row per legacy value the template actually carries.
 * criteria: the template's RoomTemplateCriteria rows ({ Discipline, FieldKey, FieldValue }).
 * items / assemblies: the catalog. equipment: the template's existing Standard Element rows.
 */
export function planTemplateConversion({ criteria = [], items = [], assemblies = [], equipment = [], layers = [] }) {
  const entities = [
    ...items.map(i => ({ ...i, Kind: "item" })),
    ...assemblies.map(a => ({ ...a, Kind: "assembly" })),
  ];
  const have = new Set(equipment.map(e => `${e.Kind}:${e.Kind === "item" ? e.ItemID : e.AssemblyID}`));
  const rows = [];
  for (const field of Object.keys(LEGACY_FIELDS)) {
    const c = criteria.find(r => r.Discipline === "arch" && r.FieldKey === field);
    const value = c?.FieldValue == null ? "" : String(c.FieldValue).trim();
    if (value === "") continue;
    const def = LEGACY_FIELDS[field];
    const base = { field, label: def.label, legacyValue: value, isCount: !!def.count };
    // Already converted: the template carries a matching element (counts are left in place on purpose, so this is
    // how a converted row stops being proposed again).
    const done = def.re && equipment.find(e => def.re.test(e.EntityName || "") && !(def.exclude && def.exclude.test(e.EntityName || "")));
    if (done) { rows.push({ ...base, skip: `Already converted — this template has "${done.EntityName}".`, converted: true }); continue; }
    if (def.count) {
      const n = +value;
      if (!(n > 0)) { rows.push({ ...base, skip: "A count of 0 needs no element." }); continue; }
      base.fixedQty = n;
    } else if (NO_ELEMENT_VALUES.test(value)) {
      rows.push({ ...base, skip: `"${value}" means no scope of this kind for CleanSpace — nothing to add.` });
      continue;
    }
    let cands;
    if (def.wall) {
      const walls = wallSystemTypes(entities, layers);
      const ow = wordsOf(value);
      cands = walls.filter(isActive)
        .map(e => ({ entity: e, score: 3 + 2 * ow.filter(w => norm(nameOf(e)).includes(w)).length }))
        .sort((a, b) => b.score - a.score).slice(0, 6);
    } else cands = candidatesFor(field, value, entities);
    const candidates = cands.map(({ entity, score }) => ({
      key: entityKeyOf(entity), kind: entity.Kind, id: entity.Kind === "item" ? entity.ItemID : entity.AssemblyID,
      label: `${entity.Code ? `${entity.Code} — ` : ""}${nameOf(entity)}${entity.Kind === "assembly" ? " · assembly" : ""}`,
      score, alreadyElement: have.has(entityKeyOf(entity)), flagPatch: flagPatchFor(entity, field), entity,
    }));
    // Preselect only when one candidate clearly leads; otherwise the admin picks.
    const lead = candidates.length === 1 || (candidates.length > 1 && candidates[0].score - candidates[1].score >= 2) ? candidates[0] : null;
    rows.push({ ...base, action: def.wall ? "wall" : "element", basis: def.basis || null, candidates, preselected: lead && !lead.alreadyElement ? lead.key : null });
  }
  return rows;
}

/** Legacy values the template still carries (drives whether the converter and the legacy list show at all). */
export const templateLegacyValues = (criteria = []) =>
  criteria.filter(r => r.Discipline === "arch" && LEGACY_FIELDS[r.FieldKey] && r.FieldValue != null && String(r.FieldValue).trim() !== "");
